import moment from 'moment-timezone';

export const formatColor = ( color ) => {
    if (!color || typeof color !== 'string') {
        return '';
    }

    const cleanColor = color.trim();

    if (cleanColor === '') {
        return '';
    }

    if (cleanColor[0] === '#') {
        return cleanColor;
    }

    return `#${cleanColor}`;
}

// Umbral (en horas) a partir del cual un cierre se considera que cubre la jornada completa del día.
const HORAS_JORNADA_COMPLETA = 20;
// Margen (en horas) para dar por cubierto el inicio o el fin del día cuando el cierre roza los bordes.
const MARGEN_HORAS_BORDE = 1;
// Tolerancia (en minutos) para considerar que un cierre arranca a las 00:00 y termina a las 23:59.
const TOLERANCIA_MINUTOS_JORNADA_COMPLETA = 1;
const MINUTOS_POR_HORA = 60;

// Un cierre cubre toda la jornada de todos sus días cuando arranca ~00:00 y termina ~23:59.
const esCierreDeJornadaCompleta = ( inicioCierre, finCierre ) => {
    const minutosDesdeInicioDelDia = inicioCierre.diff(inicioCierre.clone().startOf('day'), 'minutes');
    const minutosHastaFinDelDia = finCierre.clone().endOf('day').diff(finCierre, 'minutes');
    return minutosDesdeInicioDelDia <= TOLERANCIA_MINUTOS_JORNADA_COMPLETA
        && minutosHastaFinDelDia <= TOLERANCIA_MINUTOS_JORNADA_COMPLETA;
};

// Una franja diaria solo es válida si la hora de fin del día es posterior a la de inicio (no cruza medianoche).
const esFranjaDiariaValida = ( inicioCierre, finCierre ) => {
    const minutoDelDiaInicio = inicioCierre.hour() * MINUTOS_POR_HORA + inicioCierre.minute();
    const minutoDelDiaFin = finCierre.hour() * MINUTOS_POR_HORA + finCierre.minute();
    return minutoDelDiaFin > minutoDelDiaInicio;
};

/**
 * Determina si un cierre (ausencia o cierre de sucursal) cubre la jornada completa
 * del día seleccionado. Replica la semántica de `expandirVacacion` del backend
 * (exora-backend/helpers/vacaciones.ts) para no divergir del cálculo real de huecos:
 *  - Cierre de jornada completa (00:00 → 23:59): cubre todos sus días.
 *  - Multi-día con franja horaria válida: cada día bloquea SOLO esa franja
 *    (p.ej. 16:30-20:00), no el rango continuo; las mañanas quedan libres.
 *  - Resto (un solo día, o rango continuo que cruza medianoche): recorte del rango al día.
 * En cierres parciales devuelve false, de modo que las horas concretas las filtra
 * después el cálculo de huecos.
 */
export const cierreCubreTodaLaJornada = ( vac, fechaSeleccionada ) => {
    const inicioCierre = moment(vac.fechaInicio);
    const finCierre = moment(vac.fechaFin);
    const diaSeleccionadoInicio = fechaSeleccionada.clone().startOf('day');
    const diaSeleccionadoFin = fechaSeleccionada.clone().endOf('day');

    if (esCierreDeJornadaCompleta(inicioCierre, finCierre)) return true;

    const abarcaVariosDias = !inicioCierre.isSame(finCierre, 'day');
    const aplicaFranjaPorDia = abarcaVariosDias && esFranjaDiariaValida(inicioCierre, finCierre);

    let inicioEfectivo;
    let finEfectivo;
    if (aplicaFranjaPorDia) {
        inicioEfectivo = diaSeleccionadoInicio.clone().hour(inicioCierre.hour()).minute(inicioCierre.minute());
        finEfectivo = diaSeleccionadoInicio.clone().hour(finCierre.hour()).minute(finCierre.minute());
    } else {
        inicioEfectivo = moment.max(inicioCierre, diaSeleccionadoInicio);
        finEfectivo = moment.min(finCierre, diaSeleccionadoFin);
    }

    if (finEfectivo.isSameOrBefore(inicioEfectivo)) return false;
    const horasCierreEnDia = finEfectivo.diff(inicioEfectivo, 'hours', true);
    const horasDesdeInicioDia = Math.abs(inicioEfectivo.diff(diaSeleccionadoInicio, 'hours', true));
    const horasHastaFinDia = Math.abs(diaSeleccionadoFin.diff(finEfectivo, 'hours', true));
    return horasCierreEnDia >= HORAS_JORNADA_COMPLETA ||
        (horasDesdeInicioDia <= MARGEN_HORAS_BORDE && horasHastaFinDia <= MARGEN_HORAS_BORDE);
}

/**
 * Indica si un profesional debe quedar deshabilitado en el selector de una fecha
 * concreta por un cierre que afecta a la jornada completa de ese día:
 *  - vacaciones del propio profesional: siempre (cierre de día entero).
 *  - cierre de sucursal o ausencia parcial: solo si cubre toda la jornada del día.
 * Los cierres parciales de día de borde NO deshabilitan; sus horas las filtra el
 * cálculo de huecos horarios.
 */
export const isProfesionalEnVacacionesOAusencia = ( prof, fechaDay, vacacionesList, sucursalId ) => {
    if (!fechaDay || !vacacionesList || vacacionesList.length === 0) return false;
    const fechaSeleccionada = moment(fechaDay).startOf('day');
    const diaSemana = fechaSeleccionada.isoWeekday().toString();
    return vacacionesList.some(vac => {
        if (vac.deleted) return false;
        const esDelProfesional = vac.profesional && (
            (vac.profesional._id && String(vac.profesional._id) === String(prof._id)) ||
            (typeof vac.profesional === 'string' && String(vac.profesional) === String(prof._id))
        );
        const esDeSucursal = vac.tipo === 'sucursal' && (
            (vac.sucursal && String(vac.sucursal) === String(sucursalId)) ||
            (vac.sucursal?._id && String(vac.sucursal._id) === String(sucursalId))
        );
        if (!esDelProfesional && !esDeSucursal) return false;
        const fechaInicioVac = moment(vac.fechaInicio);
        const fechaFinVac = moment(vac.fechaFin);
        const fechaEnRango = fechaSeleccionada.isSameOrAfter(fechaInicioVac.startOf('day')) &&
            fechaSeleccionada.isSameOrBefore(fechaFinVac.endOf('day'));
        if (!fechaEnRango) return false;
        if (vac.diasSemana && vac.diasSemana.length > 0 && !vac.diasSemana.includes(diaSemana)) return false;
        if (vac.tipo === 'vacaciones') return true;
        if (vac.tipo === 'sucursal' || vac.tipo === 'ausencia') {
            return cierreCubreTodaLaJornada(vac, fechaSeleccionada);
        }
        return false;
    });
}